<template>
    <div class="ls-order-service-lead__container grid">
        <div class="grid__col-12">
            <slot></slot>
        </div>
        <div class="grid">
            <div class="grid__col-12">
                <h2
                    class="ls-condensed section-title"
                    :class="{ 'ls-order-service-lead--invalid': validation.servicesInvalid }"
                >
                    What services are you interested in?
                </h2>
            </div>
            <div class="grid__col-xs-12 grid__col-sm-10">
                <div class="ls-services__outer-container ls-button-picker">
                    <div class="ls-services__inner-container ls-margin--top">
                        <a class="ls-button ls-button--block" @click.prevent="$router.go(-1)">
                            Recurring Mowing
                        </a>
                    </div>
                    <div
                        v-for="(service, idx) in services"
                        :key="service.id"
                        class="ls-services__inner-container ls-margin--top"
                    >
                        <label
                            class="ls-button ls-button--block ls-button--checkbox"
                            :class="{
                                'ls-button--green ls-button--faded': service.selected,
                            }"
                        >
                            <input
                                v-model="service.selected"
                                type="checkbox"
                                @change="updateServices({ idx })"
                            />
                            <span class="ls-button-toggle-icon">
                                <branded-checkbox :is-active="service.selected" />
                            </span>
                            <span>{{ service.name }}</span>
                        </label>
                    </div>

                    <div
                        v-if="services[withChildrenIdx].selected"
                        class="ls-order-service-lead--child-services-wrapper"
                    >
                        <span
                            class="ls-bold ls-uppercase"
                            :class="{
                                'ls-color--green--color-dodge': isLawnStarter,
                                'll-color-primary': isLawnLove || isHomeGnome,
                                'ls-order-service-lead--invalid': validation.withChildrenInvalid,
                            }"
                            data-font-size="0"
                            >CHOOSE {{ services[withChildrenIdx].name }} TYPE</span
                        >
                        <div
                            v-for="(childService, childIdx) in services[withChildrenIdx]
                                .child_services"
                            :key="childService.str_id"
                            class="ls-margin--top"
                        >
                            <label class="ls-checkbox">
                                <input
                                    v-model="childService.selected"
                                    type="checkbox"
                                    @change="
                                        updateServices({
                                            idx: withChildrenIdx,
                                            childIdx,
                                        })
                                    "
                                />
                                <branded-checkbox
                                    :is-active="childService.selected"
                                    :is-big-icon="true"
                                />
                                <span data-font-size="-1">{{ childService.name }}</span>
                            </label>
                            <div
                                v-if="childService.selected && childService.notes_required"
                                class="ls-input__wrapper"
                                :class="{ 'ls-padding--right': !screenSize.xs }"
                            >
                                <textarea
                                    id="child-notes"
                                    v-model="childService.notes"
                                    v-ls-track.change="{
                                        target: 'order-service-lead--child-notes',
                                        attachValue: true,
                                    }"
                                    class="ls-input"
                                    type="textbox"
                                    rows="2"
                                    :class="{
                                        'ls-invalid': validation.childNotesInvalid,
                                    }"
                                    @input="
                                        updateServices({
                                            idx: withChildrenIdx,
                                            childIdx,
                                        })
                                    "
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="ls-order-service-lead--divider"></div>

        <div class="grid">
            <div class="grid__col-12">
                <h2
                    class="ls-condensed section-title"
                    :class="{ 'ls-order-service-lead--invalid': validation.budgetInvalid }"
                >
                    Additional Details
                </h2>
            </div>

            <div class="grid__col-sm-10">
                <label class="ls-label ls-margin--top" for="notes"
                    >Other details about what you need</label
                >
                <div class="ls-input__wrapper" :class="{ 'ls-padding--right': !screenSize.xs }">
                    <textarea
                        id="notes"
                        v-model="notes"
                        v-ls-track.change="{
                            target: 'order-service-lead--notes',
                            attachValue: true,
                        }"
                        class="ls-input"
                        type="textbox"
                        rows="3"
                        test-id="notes"
                        @input="updateNotes"
                    ></textarea>
                </div>

                <label class="ls-label ls-margin--top" for="budget">Budget</label>
                <div
                    class="ls-input__wrapper for-select"
                    :class="{ 'ls-margin--right': !screenSize.xs }"
                >
                    <select
                        id="budget"
                        v-model="budget"
                        v-ls-track.change="{
                            target: 'order-service-lead--budget',
                            attachValue: true,
                        }"
                        class="ls-input"
                        test-id="budget"
                        :class="{ 'ls-invalid': validation.budgetInvalid }"
                        @change="updateBudget"
                    >
                        <option :value="{}" selected disabled>- Select -</option>
                        <option
                            v-for="budget in budgetOptions"
                            :key="budget.label"
                            :value="budget.value"
                        >
                            {{ budget.label }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import { ORDER_SERVICE_LEAD__UPDATE_SERVICE } from '@/store/types';
import lsTrackService from '@/services/ls-track/ls-track.service';
import brandedCheckbox from '@/components/original-mydot/branded-checkbox/BrandedCheckbox.vue';

export default {
    name: 'LsOrderServiceLead',
    components: {
        brandedCheckbox,
    },
    data() {
        return {
            // form
            services: [],
            notes: '',
            budget: {},

            budgetOptions: [],
            validation: {
                servicesInvalid: undefined,
                withChildrenInvalid: undefined,
                childNotesInvalid: undefined,
                budgetInvalid: undefined,
            },
            withChildrenIdx: null,
            childNotesIdx: null,
        };
    },
    computed: {
        ...mapState(['orderServiceLead', 'screenSize']),
    },
    methods: {
        ...mapActions({
            updateService: ORDER_SERVICE_LEAD__UPDATE_SERVICE,
        }),
        updateServicesValidaton() {
            this.validation.servicesInvalid = !_.some(this.orderServiceLead.services, {
                selected: true,
            });
        },
        updateBudgetValidation() {
            this.validation.budgetInvalid = _.isEmpty(
                this.orderServiceLead.additionalDetails.budget
            );
        },
        updateWithChildrenValidation({ skipDirtyCheck } = {}) {
            if (!skipDirtyCheck && _.isUndefined(this.validation.withChildrenInvalid)) {
                this.validation.withChildrenInvalid = null;
                return;
            }
            const serviceWithChildren = this.orderServiceLead.services[this.withChildrenIdx];
            this.validation.withChildrenInvalid =
                Boolean(serviceWithChildren.selected) &&
                !_.some(serviceWithChildren.child_services, {
                    selected: true,
                });
        },
        updateChildNotesValidation({ skipDirtyCheck } = {}) {
            if (!skipDirtyCheck && _.isUndefined(this.validation.childNotesInvalid)) {
                this.validation.childNotesInvalid = null;
                return;
            }
            const serviceWithChildren = this.orderServiceLead.services[this.withChildrenIdx];
            const childServiceWithNotes = serviceWithChildren.child_services[this.childNotesIdx];

            this.validation.childNotesInvalid =
                Boolean(serviceWithChildren.selected) &&
                Boolean(childServiceWithNotes.selected) &&
                childServiceWithNotes.notes === '';
        },
        checkFormValidation() {
            this.updateServicesValidaton();
            this.updateBudgetValidation();
            this.updateWithChildrenValidation({ skipDirtyCheck: true });
            this.updateChildNotesValidation({ skipDirtyCheck: true });

            return (
                !this.validation.servicesInvalid &&
                !this.validation.budgetInvalid &&
                !this.validation.withChildrenInvalid &&
                !this.validation.childNotesInvalid
            );
        },
        updateServices({ idx, childIdx }) {
            const service = _.isNil(childIdx)
                ? this.services[idx]
                : this.services[idx].child_services[childIdx];

            lsTrackService.track({
                type: 'change',
                target: 'order-service-lead--option',
                data: {
                    str_id: service.str_id,
                },
            });

            this.updateService({
                idx,
                childIdx,
                payload: {
                    selected: service.selected,
                    notes: service.notes,
                },
            });
            this.updateServicesValidaton();

            if (idx === this.withChildrenIdx) {
                this.updateWithChildrenValidation();
                if (childIdx === this.childNotesIdx) {
                    this.updateChildNotesValidation();
                }
            }
        },
        updateNotes() {
            this.updateService({
                payload: {
                    notes: this.notes,
                },
            });
        },
        updateBudget() {
            this.updateService({
                payload: {
                    budget: this.budget,
                },
            });
            this.updateBudgetValidation();
        },
    },
    created() {
        const { orderServiceLead } = this;
        this.services = _.map(orderServiceLead.services, (service, idx) => {
            if (_.size(service.child_services)) {
                this.withChildrenIdx = idx;
            }

            const child_services = _.map(service.child_services, (childService, childIdx) => {
                if (childService.notes_required) {
                    this.childNotesIdx = childIdx;
                }
                return {
                    ...childService,
                    selected: Boolean(childService.selected),
                    notes: childService.notes_required ? childService.notes || '' : null,
                    notes_required: childService.notes_required,
                };
            });

            return {
                ...service,
                selected: Boolean(service.selected),
                child_services,
            };
        });

        this.notes = orderServiceLead.additionalDetails.notes;

        this.budgetOptions = _.map(orderServiceLead.budgets, (budget, idx) => {
            const low = _.replace(budget.low, /\B(?=(\d{3})+(?!\d))/g, ',');
            const high = _.replace(budget.high, /\B(?=(\d{3})+(?!\d))/g, ',');
            let label = `$${low} – $${high}`;

            if (idx === 0) {
                label = `Up to $${high}`;
            } else if (idx === _.size(orderServiceLead.budgets) - 1) {
                label = `$${low}+`;
            }

            return {
                label,
                value: budget,
            };
        });

        this.budget = orderServiceLead.additionalDetails.budget;
    },
};
</script>

<style lang="scss">
.ls-input {
    resize: none;
}

.ls-services__outer-container {
    display: flex;
    flex-wrap: wrap;
}

.ls-services__inner-container {
    flex: 0 0 auto;
    padding-right: rem(0);
    width: 50%;

    @include max-screen(breakpoint(xs, max)) {
        padding-right: 0;
        width: 100%;
    }
}

.ls-order-service-lead--invalid {
    color: palette(red) !important;
}

.ls-order-service-lead--divider {
    background-color: #d5dbd9;
    height: 1px;
    margin: 16px 10px;
    width: 100%;
}

.ls-checkbox {
    display: flex;
    margin-left: 0;
    margin-bottom: rem(-3);
    padding-right: rem(-3);
    user-select: none;

    i {
        padding-right: rem(0);
        margin-top: 3px;
        font-size: rem(0);
    }

    span:before {
        // override spacing used elsewhere since we're using fa checkboxes
        content: '';
    }

    input {
        display: none;
    }
}

.ls-order-service-lead--child-services-wrapper {
    border: 1px solid #abb8b2;
    padding: 15px;
    margin-top: 15px;
    width: 100%;
}
.themed-app {
    .ls-order-service-lead--invalid {
        color: $theme-color-red !important;
    }

    .ls-order-service-lead--divider {
        background-color: $theme-color-gray;
    }

    .ls-order-service-lead--child-services-wrapper {
        border-color: $theme-color-gray-dark;
    }
}

.themed-lawnlove {
    .ls-order-service-lead--child-services-wrapper {
        .branded-checkbox {
            .fa {
                margin-top: 0;
                margin-right: rem(-5);
            }
        }
    }
}

.themed-homgnome {
    .ls-services__inner-container {
        .ls-button {
            .ls-button--faded {
                background-color: $theme-color-primary !important;
            }
        }
    }
}
</style>
